import { Injectable, signal, computed, inject } from '@angular/core';
import { IDocCase } from '../models/documentCase.modal';
import { IDocumentPricesResponse } from '../models/document-prices.model';
import { GetCountryDocuments } from './get-country-documents';
import {DayTime} from '../../shared/client-service/day-time/day-time'

export interface SelectedDoc {
    doc: IDocCase;
    quantity: number;
}

@Injectable({ providedIn: 'root' })
export class OrderSelectionService {
    private getCountryDocsService = inject(GetCountryDocuments);

    dayTimeService = inject(DayTime);

    /** Map of docId → SelectedDoc */
    private _selectedDocs = signal<Map<number, SelectedDoc>>(new Map());

    async fetchDocumentPrices(countOverride?: number) {
        if (typeof document === 'undefined') return null;
        const hiddenInput = document.querySelector<HTMLInputElement>('input.orderId');
        if (!hiddenInput) return null;

        const orderId = Number(hiddenInput.dataset['id']);
        const dotNetCountryId = Number(hiddenInput.dataset['dotnetcountryid']);
        const count = countOverride !== undefined ? countOverride : this.totalDocCount();

        this.isLoadingPrices.set(true);
        
        let hasExp = false
        let expDays = 0
        try {
            const res = await this.getCountryDocsService.getDocumentPrices(orderId, dotNetCountryId, count);
            if (res) {
                res.services.forEach((service) => {
                    if (service.ExpeditedOptions !== null) {
                        hasExp = true
                        expDays += service.ExpeditedOptions.ExpeditedWorkingDays
                    }                        

                    service.Fee = service.ExpeditedOptions == null ? service.Fee : (service.Fee + service.ExpeditedOptions.UsingExpeditedFees);
                    service.TotalFee = service.Fee * service.Count;
                    service.Time = service.ExpeditedOptions == null ? service.Time : service.ExpeditedOptions.ExpeditedWorkingDays;
                });
                res.TotalFee = res.services.reduce((acc, service) => acc + service.TotalFee, 0);
                res.TotalTime = res.services.reduce((acc, service) => acc + service.Time, 0);

                if (countOverride === undefined) {
                    this.documentPrices.set(res);

                    this.date.set(this.dayTimeService.calcEndDate(res.TotalTime, hasExp, expDays));
                    
                    this.hasExp.set(hasExp)
                }
                return res;
            }
        } catch (error) {
            console.error('[OrderSelectionService] Error fetching document prices:', error);
        } finally {
            this.isLoadingPrices.set(false);
        }
        return null;
    }

    /** Whether any doc is selected */
    hasSelection = computed(() => this._selectedDocs().size > 0);

    /** Manually specified document count, if set from the dropdown */
    manualDocCount = signal<number | null>(null);

    /** Total number of individual documents (sum of quantities or manual override) */
    totalDocCount = computed(() => {
        const manual = this.manualDocCount();
        if (manual !== null) {
            return manual;
        }
        let total = 0;
        for (const entry of this._selectedDocs().values()) {
            total += entry.quantity;
        }
        return total;
    });

    /** The selected docs as an array */
    selectedDocs = computed(() => Array.from(this._selectedDocs().values()));

    /** Highest restriction level among selected docs:
     *  Case 3 (Not accepted as digital) > Case 2 (Digital but physical notary required) > Case 1 (Digital).
     *  0 = No selection (default to Case 1 logic UI-side if manual count) 
     */
    highestRestrictionLevel = computed(() => {
        let level = 0;
        for (const entry of this._selectedDocs().values()) {
            const doc = entry.doc;
            if (!doc.is_digital_accepted) {
                level = Math.max(level, 3);
            } else if (doc.is_digital_accepted && doc.is_physical_notary_required) {
                level = Math.max(level, 2);
            } else if (doc.is_digital_accepted && !doc.is_physical_notary_required) {
                level = Math.max(level, 1);
            }
        }
        return level === 0 ? 1 : level; // Default to 1 if no specific docs are selected yet
    });

    /** Whether the order-process panel should be visible */
    showOrderProcess = signal(false);
    startWithCount = signal(true);
    showCase2Modal = signal(false);

    /** Document prices fetched from the API */
    documentPrices = signal<IDocumentPricesResponse | null>(null);

    date = signal<Date | null>(null);

    hasExp = signal<boolean>(false);

    /** Extra pages across all uploaded files */
    extraPages = signal<number>(0);

    addExtraPages(count: number) {
        this.extraPages.set(this.extraPages() + count);
    }

    /** Whether document prices are currently being loaded */
    isLoadingPrices = signal(false);

    /** Toggle a doc in/out of the selection */
    toggleDoc(doc: IDocCase, checked: boolean) {
        const current = new Map(this._selectedDocs());
        const id = Number(doc.id);
        if (checked) {
            current.set(id, { doc, quantity: 1 });
        } else {
            current.delete(id);
        }
        this._selectedDocs.set(current);
    }

    /** Get current quantity of a specific doc */
    getQuantity(docId: number | string): number {
        const entry = this._selectedDocs().get(Number(docId));
        return entry ? entry.quantity : 0;
    }

    /** Update the quantity for a selected doc */
    setQuantity(docId: number | string, quantity: number) {
        const current = new Map(this._selectedDocs());
        const id = Number(docId);
        const entry = current.get(id);
        if (entry) {
            current.set(id, { ...entry, quantity: Math.max(0, quantity) });
            this._selectedDocs.set(current);
        }
    }

    /** Reveal the order-process panel */
    startOrder() {
        this.showOrderProcess.set(true);
    }

    /** Reset all selections and hide the order panel */
    reset() {
        this._selectedDocs.set(new Map());
        this.showOrderProcess.set(false);
        this.manualDocCount.set(null);
        this.documentPrices.set(null);
        this.isLoadingPrices.set(false);
        this.showCase2Modal.set(false);
        this.extraPages.set(0);
    }
}
